import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import type { INotificationFilters, NotificationType, NotificationStatus } from "@/interfaces/notification.interface";

export interface NotificationFilterDialogProps {
  open: boolean;
  onClose: () => void;
  filters: INotificationFilters;
  onApply: (filters: INotificationFilters) => void;
}

const typeOptions: { value: NotificationType; label: string }[] = [
  { value: "Alerta", label: "Alerta" },
  { value: "Mensagem", label: "Mensagem" },
  { value: "Pagamento", label: "Pagamento" },
];

const statusOptions: { value: NotificationStatus; label: string }[] = [
  { value: "Pendente", label: "Pendente" },
  { value: "Lida", label: "Lida" },
];

export function NotificationFilterDialog({
  open,
  onClose,
  filters,
  onApply,
}: NotificationFilterDialogProps) {
  const [localFilters, setLocalFilters] = useState<INotificationFilters>(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters, open]);

  const handleTypeToggle = (type: NotificationType) => {
    setLocalFilters((prev) => ({
      ...prev,
      type: prev.type.includes(type)
        ? prev.type.filter((t) => t !== type)
        : [...prev.type, type],
    }));
  };

  const handleStatusToggle = (status: NotificationStatus) => {
    setLocalFilters((prev) => ({
      ...prev,
      status: prev.status.includes(status)
        ? prev.status.filter((s) => s !== status)
        : [...prev.status, status],
    }));
  };

  const handleApply = () => {
    onApply(localFilters);
    onClose();
  };

  const handleClear = () => {
    const clearedFilters: INotificationFilters = {
      type: [],
      status: [],
      dateFrom: "",
      dateTo: "",
    };
    setLocalFilters(clearedFilters);
    onApply(clearedFilters);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#15203C] border-[#3D3D3D] max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white text-lg font-semibold">
            Filtros
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Type Filters */}
          <div>
            <p className="text-sm font-medium text-white mb-3">Tipo</p>
            <div className="space-y-2">
              {typeOptions.map((option) => (
                <Checkbox
                  key={option.value}
                  id={`type-${option.value}`}
                  checked={localFilters.type.includes(option.value)}
                  onCheckedChange={() => handleTypeToggle(option.value)}
                  label={option.label}
                />
              ))}
            </div>
          </div>

          {/* Status Filters */}
          <div>
            <p className="text-sm font-medium text-white mb-3">Status</p>
            <div className="space-y-2">
              {statusOptions.map((option) => (
                <Checkbox
                  key={option.value}
                  id={`status-${option.value}`}
                  checked={localFilters.status.includes(option.value)}
                  onCheckedChange={() => handleStatusToggle(option.value)}
                  label={option.label}
                />
              ))}
            </div>
          </div>

          {/* Date Range Filters */}
          <div>
            <p className="text-sm font-medium text-white mb-3">Período</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="dateFrom" className="text-xs text-gray-400 mb-1 block">
                  De
                </label>
                <Input
                  id="dateFrom"
                  type="date"
                  value={localFilters.dateFrom}
                  onChange={(e) =>
                    setLocalFilters((prev) => ({ ...prev, dateFrom: e.target.value }))
                  }
                  className="bg-[#0C1427] border-[#5D5D5D] h-[50px] text-white"
                />
              </div>
              <div>
                <label htmlFor="dateTo" className="text-xs text-gray-400 mb-1 block">
                  Até
                </label>
                <Input
                  id="dateTo"
                  type="date"
                  value={localFilters.dateTo}
                  onChange={(e) =>
                    setLocalFilters((prev) => ({ ...prev, dateTo: e.target.value }))
                  }
                  className="bg-[#0C1427] border-[#5D5D5D] h-[50px] text-white"
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={handleClear} className="flex-1">
            Limpar
          </Button>
          <Button onClick={handleApply} className="flex-1">
            Aplicar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
